"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/react";
import { cn } from "@/lib/utils";
import { DEFAULT_MAX_CHARS, isTruncated, shownText, visibleText } from "@/lib/copy-field";

type CopyState = "idle" | "copied" | "failed";

export type CopyFieldProps = {
    /** Visible name of the value ("Public endpoint URL"); also the accessible name of the copy button. */
    label: string;
    /** What the clipboard receives, always in full. */
    value: string;
    /** Text to show instead of `value` (e.g. a command whose token is masked). */
    display?: string;
    /** Show the fixed-length mask instead of `value`. */
    masked?: boolean;
    /** Middle-truncate a long shown text (URLs, ids). The full text stays in the title and on copy. */
    truncate?: boolean;
    maxChars?: number;
    /** `inline`: one line next to the button. `block`: a scrollable code block (commands, snippets). */
    layout?: "inline" | "block";
    /** Accessible name of the copy button when the label alone is ambiguous (default: "Copy <label>"). */
    copyLabel?: string;
    /** Keep the label for assistive technology only. */
    hideLabel?: boolean;
    /** Extra controls placed before the copy button (the token's reveal switch). */
    actions?: React.ReactNode;
    className?: string;
};

/**
 * The one way to show a value the user is meant to copy (#108): a label, the
 * value (middle-truncated when long, masked when secret), a copy button with
 * an accessible name, and a polite "Copied" announcement.
 */
export const CopyField = ({
    label,
    value,
    display,
    masked,
    truncate = false,
    maxChars = DEFAULT_MAX_CHARS,
    layout = "inline",
    copyLabel,
    hideLabel = false,
    actions,
    className,
}: CopyFieldProps) => {
    const t = useT();
    const labelId = useId();
    const [state, setState] = useState<CopyState>("idle");
    const [expanded, setExpanded] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (timer.current) clearTimeout(timer.current);
        },
        [],
    );

    const copy = async () => {
        let next: CopyState = "copied";
        try {
            await navigator.clipboard.writeText(value);
        } catch {
            next = "failed";
        }
        setState(next);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setState("idle"), 2000);
    };

    const shown = shownText({ value, display, masked });
    // Truncation applies to the inline layout; a block is scrollable and always shown in full.
    const canTruncate = truncate && layout === "inline";
    const hidden = isTruncated(shown, canTruncate, maxChars);
    const text = visibleText(shown, { truncate: canTruncate, maxChars, expanded });
    // The title reveals the full text on hover, but never the secret behind a mask.
    const title = hidden && !expanded ? shown : undefined;
    const copied = state === "copied";

    const valueNode =
        layout === "block" ? (
            // Keyboard users must be able to scroll a wide block (WCAG 2.1.1), so it takes focus; a focusable
            // element needs a role and a name, so the block is a labelled region (<section> cannot wrap the <pre>
            // and keep the flex layout).
            // biome-ignore lint/a11y/useSemanticElements: the element is the <pre>, which must stay a <pre> for its text semantics
            <pre
                role="region"
                // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable with the keyboard
                tabIndex={0}
                aria-labelledby={labelId}
                className="min-w-0 flex-1 overflow-x-auto whitespace-pre rounded-md bg-muted p-3 font-mono text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <code>{shown}</code>
            </pre>
        ) : (
            <code
                title={title}
                className="min-w-0 flex-1 break-all rounded-md bg-muted px-3 py-2 font-mono text-xs text-foreground"
            >
                {/* The visible text may be truncated: assistive technology reads the full text, and a truncated text is not selectable (use Copy or Show full). */}
                <span aria-hidden="true" className={hidden && !expanded ? "select-none" : undefined}>
                    {text}
                </span>
                <span className="sr-only">{shown}</span>
            </code>
        );

    return (
        <div className={cn("min-w-0", className)}>
            <span id={labelId} className={cn("mb-1 block text-xs font-medium text-muted-foreground", hideLabel && "sr-only")}>
                {label}
            </span>
            <div className={cn("flex gap-2", layout === "block" ? "items-start" : "items-center")}>
                {valueNode}
                {actions}
                <Button
                    type="button"
                    variant="outline"
                    onClick={copy}
                    aria-label={copyLabel ?? t("token.copyField.copyLabel", { label })}
                    className={cn(
                        "h-11 min-w-11 shrink-0 px-3 sm:h-9",
                        copied && "border-success/30 bg-success-subtle text-success hover:bg-success-subtle",
                    )}
                >
                    {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                    <span className={cn("ml-2", state === "failed" ? "inline" : "hidden sm:inline")} aria-hidden="true">
                        {copied
                            ? t("token.copyField.copied")
                            : state === "failed"
                              ? t("token.copyField.copyFailed")
                              : t("token.copyField.copy")}
                    </span>
                </Button>
            </div>
            {hidden && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    className="mt-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    {expanded ? t("token.copyField.showLess") : t("token.copyField.showFull")}
                </button>
            )}
            {/* Always mounted so the change is announced (WCAG 4.1.3). */}
            <span role="status" aria-live="polite" className="sr-only">
                {state === "copied" ? t("token.copyField.copied") : state === "failed" ? t("token.copyField.copyFailedHint") : ""}
            </span>
        </div>
    );
};

export default CopyField;
